import type {
  BillingMethod,
  StripeCardMethod,
} from '@/features/billing/profile/BillingMethod'
import { computed } from 'mobx'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { task } from 'mobx-task'
import { type SnackbarFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'

const SNACKBAR_KEY = Symbol('Billing Method Snackbar')

/**
 * View model for a billing method item.
 */
export class BillingMethodListItemViewModel {
  readonly removeModal = new ModalViewModel()

  constructor(
    private readonly _billingProfileStore: BillingProfileStore,
    private readonly _newSnackbar: SnackbarFactory,
    public readonly method: BillingMethod,
  ) {}

  @computed
  get id() {
    return this.method.id
  }

  @computed
  get isDefault() {
    return this.method.isDefault
  }

  @computed
  get isCard() {
    return (
      this.method.type.type === 'Stripe' ||
      this.method.type.type === 'StripeCardPaymentMethod'
    )
  }

  @computed
  get stripeCardBillingMethod() {
    return this.isCard ? (this.method.type as StripeCardMethod) : null
  }

  @computed
  get cardBrand() {
    return this.stripeCardBillingMethod?.brand ?? 'Unknown'
  }

  @computed
  get cardExpMonth() {
    return this.stripeCardBillingMethod?.expMonth ?? 1
  }

  @computed
  get cardExpYear() {
    return this.stripeCardBillingMethod?.expYear ?? 9999
  }

  @computed
  get cardLast4() {
    return this.stripeCardBillingMethod?.last4 ?? '9999'
  }

  /**
   * Sets the method as the default.
   */
  setAsDefault = task.resolved(async () => {
    const snackbar = this._newSnackbar((s) => s.withKey(SNACKBAR_KEY))
    await this._billingProfileStore
      .changeDefaultMethod(this.method)
      .catch((err) => {
        snackbar.fromError(err)
        throw err
      })
  })

  /**
   * Removes the billing method.
   */
  remove = task.resolved(async () => {
    const snackbar = this._newSnackbar((s) => s.withKey(SNACKBAR_KEY))
    await this._billingProfileStore
      .removeBillingMethod(this.method)
      .catch((err) => {
        snackbar.fromError(err)
        throw err
      })
    this.removeModal.close()
    snackbar
      .type('success')
      .autoDismiss()
      .saying(
        'Success!',
        `The payment method ending in ${this.cardLast4} has been removed.`,
      )
      .show()
  })
}
